import React, { useEffect } from "react";
import {
  Stack,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Upload as UploadIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  AutoFixHigh as ClassifyIcon,
  NavigateNext as BreadcrumbIcon,
  HelpOutline as UnclassifiedIcon,
  SmartToy as OpenAiIcon,
  Edit as ManualIcon,
  History as RecentIcon,
} from "@mui/icons-material";
import { usePageTitle } from "../context/PageTitleContext";
import CategoryTreeFilter from "../components/CategoryTreeFilter";
import { SOURCE_COLORS } from "./category-review/types";
import { useCategoryReviewData } from "./category-review/useCategoryReviewData";
import { CategoryCell } from "./category-review/CategoryCell";
import { CategoryReviewImportDialog } from "./category-review/CategoryReviewImportDialog";
import { SummaryCards } from "./category-review/SummaryCards";

export default function CategoryReviewPage() {
  const { setTitle } = usePageTitle();
  const data = useCategoryReviewData();
  const {
    products,
    total,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    search,
    searchInput,
    setSearchInput,
    categoryFilter,
    setCategoryFilter,
    sourceFilter,
    setSourceFilter,
    llmResponseFilter,
    setLlmResponseFilter,
    loading,
    error,
    setError,
    summary,
    existingCategories,
    categoryCounts,
    llmResponses,
    editingId,
    editValue,
    setEditValue,
    saving,
    importOpen,
    setImportOpen,
    importResult,
    setImportResult,
    importing,
    fileInputRef,
    selectedIds,
    classifying,
    snackbar,
    setSnackbar,
    handleToggleSelect,
    handleSelectAll,
    handleClassifySelected,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleExport,
    handleImport,
  } = data;

  useEffect(() => {
    setTitle("Revision de Categorias");
  }, [setTitle]);

  const sourceChipColor = (s: string | null) =>
    SOURCE_COLORS[s || ""] || "default";


  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      {/* Summary cards (extraídas P7) */}
      {summary && (
        <SummaryCards
          summary={summary}
          llmResponses={llmResponses}
          onClickRecentExecutions={() => {
            setSourceFilter("recent");
            setLlmResponseFilter(null);
          }}
        />
      )}

      {/* Filters + actions */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ flexWrap: "wrap", gap: 1 }}
      >
        <TextField
          size="small"
          placeholder="Buscar producto..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <CategoryTreeFilter
          categories={existingCategories}
          categoryCounts={categoryCounts}
          value={categoryFilter}
          onChange={(val) => setCategoryFilter(val)}
          unclassifiedCount={summary?.unclassified}
        />
        <Stack direction="row" spacing={0.5}>
          {(["", "openai", "manual", "unclassified", "recent"] as const).map(
            (s) => (
              <Chip
                key={s || "all"}
                label={s === "recent" ? "Recientes" : s || "Todos"}
                size="small"
                {...(s === "recent"
                  ? { icon: <RecentIcon sx={{ fontSize: 16 }} /> }
                  : {})}
                variant={sourceFilter === s ? "filled" : "outlined"}
                color={
                  s === "recent"
                    ? "secondary"
                    : s
                      ? SOURCE_COLORS[s] || "default"
                      : "default"
                }
                onClick={() => {
                  setSourceFilter(s);
                  if (s !== "recent") setLlmResponseFilter(null);
                }}
              />
            ),
          )}
        </Stack>
        {/* LLM run filter — visible when source=recent or when there are runs */}
        {(sourceFilter === "recent" || llmResponseFilter) &&
          llmResponses.length > 0 && (
            <Autocomplete
              size="small"
              options={llmResponses}
              getOptionLabel={(opt) => {
                const date = new Date(opt.created_at);
                const dateStr = date.toLocaleDateString("es-MX", {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                });
                const cost = opt.cost_estimate
                  ? `$${parseFloat(opt.cost_estimate).toFixed(4)}`
                  : "";
                return `#${opt.id} — ${dateStr} — ${opt.products_linked} prod. ${cost ? `— ${cost}` : ""} (${opt.status})`;
              }}
              value={
                llmResponses.find((r) => r.id === llmResponseFilter) || null
              }
              onChange={(_e, newVal) => {
                setLlmResponseFilter(newVal?.id || null);
                if (newVal && sourceFilter !== "recent")
                  setSourceFilter("recent");
              }}
              sx={{ minWidth: 320 }}
              renderInput={(params) => {
                const {
                  InputLabelProps: _label,
                  InputProps,
                  inputProps,
                  ...rest
                } = params;
                return (
                  <TextField
                    {...rest}
                    slotProps={{
                      input: { ...InputProps },
                      htmlInput: { ...inputProps },
                    }}
                    placeholder="Filtrar por ejecucion LLM..."
                    size="small"
                  />
                );
              }}
            />
          )}
        <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
          {selectedIds.size > 0 && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={
                classifying ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <ClassifyIcon />
                )
              }
              onClick={handleClassifySelected}
              disabled={classifying}
            >
              {classifying
                ? "Clasificando..."
                : `Clasificar (${selectedIds.size})`}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Exportar CSV
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setImportOpen(true)}
          >
            Importar CSV
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{ maxHeight: "calc(100vh - 340px)" }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: "3%" }}>
                <Checkbox
                  size="small"
                  indeterminate={
                    selectedIds.size > 0 && selectedIds.size < products.length
                  }
                  checked={
                    products.length > 0 && selectedIds.size === products.length
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 700, width: "30%" }}>
                Producto
              </TableCell>
              <TableCell sx={{ fontWeight: 700, width: "25%" }}>
                Categoria
              </TableCell>
              <TableCell sx={{ fontWeight: 700, width: "6%" }}>
                Fuente
              </TableCell>
              <TableCell sx={{ fontWeight: 700, width: "5%" }}>ABC</TableCell>
              <TableCell sx={{ fontWeight: 700, width: "12%" }}>
                Cat. Odoo 16
              </TableCell>
              <TableCell sx={{ fontWeight: 700, width: "12%" }}>
                Cat. Odoo 18
              </TableCell>
              <TableCell sx={{ fontWeight: 700, width: "7%" }}>
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Sin resultados</Typography>
                </TableCell>
              </TableRow>
            ) : (
              products.map((p) => {
                const isEditing = editingId === p.unified_product_id;
                return (
                  <TableRow
                    key={p.unified_product_id}
                    sx={{
                      bgcolor: selectedIds.has(p.id)
                        ? "rgba(25, 118, 210, 0.08)"
                        : p.category_source === "manual"
                          ? "rgba(33, 150, 243, 0.04)"
                          : undefined,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.has(p.id)}
                        onChange={() => handleToggleSelect(p.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={p.unified_name}>
                        {p.unified_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <CategoryCell
                        product={p}
                        isEditing={editingId === p.unified_product_id}
                        editValue={editValue}
                        setEditValue={setEditValue}
                        existingCategories={existingCategories}
                        onStartEdit={handleStartEdit}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={p.category_source || "—"}
                        size="small"
                        color={sourceChipColor(p.category_source)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {p.abc_classification ? (
                        <Chip
                          label={p.abc_classification}
                          size="small"
                          color={
                            p.abc_classification === "A"
                              ? "error"
                              : p.abc_classification === "B"
                                ? "warning"
                                : "default"
                          }
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap color="text.secondary">
                        {p.category_16 || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap color="text.secondary">
                        {p.category_18 || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Guardar">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() =>
                                handleSaveEdit(p.unified_product_id)
                              }
                              disabled={saving || !editValue}
                            >
                              {saving ? (
                                <CircularProgress size={16} />
                              ) : (
                                <CheckIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar">
                            <IconButton size="small" onClick={handleCancelEdit}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Chip
                          label="Editar"
                          size="small"
                          variant="outlined"
                          onClick={() => handleStartEdit(p)}
                          sx={{ cursor: "pointer" }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="Filas:"
      />

      {/* Import Dialog (extraído P7) */}
      <CategoryReviewImportDialog
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          setImportResult(null);
        }}
        fileInputRef={fileInputRef}
        importing={importing}
        importResult={importResult}
        onImport={handleImport}
      />

      {/* Snackbar for non-blocking notifications */}
      <Snackbar
        open={snackbar?.open ?? false}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar?.severity ?? "info"}
          onClose={() => setSnackbar(null)}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
